import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'

import logo from '../assets/img/logoUrbanerBlackH.png'
import { loginUser } from '../actions/userActions'

const LoginPage = ({ isAuthenticated, isFetching, errors = {}, login }) => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [emailTouched, setEmailTouched] = useState(false)
  const [emailValidation, setEmailValidation] = useState({
    isValid: true,
    message: ''
  })
  const history = useHistory()
  const errorRef = React.useRef(null)

  // componentDidMount + componentWillUnmount
  useEffect(() => {
    document.body.classList.add('gray-bg')

    return () => {
      document.body.classList.remove('gray-bg')
    }
  }, [])

  // componentDidUpdate - Redirect when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      history.push('/')
    }
  }, [isAuthenticated, history])

  // Lleva el error a la vista y lo sacude, para que no pase desapercibido.
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) return undefined

    console.error('❌ Error de login:', errors)

    // Los dos temporizadores se guardan para poder cancelarlos: sin eso, salir
    // de la pantalla entre medias los dejaba corriendo, y el doble montaje de
    // StrictMode encadenaba dos sacudidas sobre el mismo elemento.
    let quitarSacudida = null

    const llevarALaVista = setTimeout(() => {
      if (!errorRef.current) return
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      errorRef.current.classList.add('shake-animation')
      quitarSacudida = setTimeout(() => {
        errorRef.current?.classList.remove('shake-animation')
      }, 500)
    }, 100)

    return () => {
      clearTimeout(llevarALaVista)
      clearTimeout(quitarSacudida)
    }
  }, [errors])

  // Validación de email en tiempo real
  const validateEmail = emailValue => {
    if (!emailValue) {
      return { isValid: false, message: '' }
    }

    // Regex básico para validar email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!emailRegex.test(emailValue)) {
      return {
        isValid: false,
        message: 'El formato del correo electrónico no es válido'
      }
    }

    return { isValid: true, message: '' }
  }

  const handleChange = e => {
    const { name, value } = e.target
    if (name === 'email') {
      setEmail(value)
      // Validar solo si el campo ha sido tocado
      if (emailTouched) {
        const validation = validateEmail(value)
        setEmailValidation(validation)
      }
    } else if (name === 'password') {
      setPassword(value)
    }
  }

  const handleEmailBlur = () => {
    setEmailTouched(true)
    const validation = validateEmail(email)
    setEmailValidation(validation)
  }

  const handleSubmit = e => {
    e.preventDefault()

    // Validar email antes de enviar
    const validation = validateEmail(email)
    if (!validation.isValid) {
      setEmailTouched(true)
      setEmailValidation(validation)
      return
    }
    login({ email, password })
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Función para humanizar mensajes de error
  const getErrorMessage = errors => {
    if (errors.non_field_errors) {
      const msg = errors.non_field_errors[0]
      if (msg.includes('credentials') || msg.includes('credenciales')) {
        return 'El correo o la contraseña son incorrectos. Por favor, intenta de nuevo.'
      }
      return msg
    }
    if (errors.detail) {
      if (
        errors.detail.includes('credentials') ||
        errors.detail.includes('credenciales')
      ) {
        return 'El correo o la contraseña son incorrectos. Por favor, intenta de nuevo.'
      }
      if (
        errors.detail.includes('expired') ||
        errors.detail.includes('expirado')
      ) {
        return 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.'
      }
      return errors.detail
    }
    return null
  }

  const btnClass = classNames('btn btn-primary block full-width m-b', {
    disabled: isFetching
  })

  const errorMessage = getErrorMessage(errors)

  return (
    <div className="middle-box text-center loginscreen ">
      <div>
        <div>
          <h1 className="logo-name">
            <img alt="logo" src={logo} style={{ width: '300px' }} />
          </h1>
        </div>

        {errorMessage && (
          <div
            ref={errorRef}
            role="alert"
            className="alert alert-danger error-message-fade-in"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '20px',
              padding: '15px',
              fontSize: '14px',
              fontWeight: '500',
              boxShadow: '0 2px 8px rgba(220, 53, 69, 0.25)'
            }}
          >
            <i
              className="fa fa-exclamation-circle"
              aria-hidden="true"
              style={{ fontSize: '18px' }}
            />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            {errors.email &&
              errors.email.map((message, index) => (
                <span
                  key={index}
                  className="help-block text-danger pull-left"
                  id="email-error"
                >
                  <i className="fa fa-exclamation-circle" aria-hidden="true" />{' '}
                  {message}
                </span>
              ))}
            {/* Validación en tiempo real */}
            {emailTouched &&
              !emailValidation.isValid &&
              emailValidation.message && (
                <span
                  className="help-block text-warning pull-left"
                  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
                >
                  <i
                    className="fa fa-exclamation-triangle"
                    aria-hidden="true"
                  />
                  {emailValidation.message}
                </span>
              )}
            {emailTouched && emailValidation.isValid && email && (
              <span
                className="help-block text-success pull-left"
                style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <i className="fa fa-check-circle" aria-hidden="true" />
                Formato válido
              </span>
            )}
            <input
              autoFocus
              type="email"
              name="email"
              className={classNames('form-control', {
                'input-validation-error':
                  emailTouched && !emailValidation.isValid,
                'input-validation-success':
                  emailTouched && emailValidation.isValid && email
              })}
              placeholder="Correo electrónico"
              required=""
              value={email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              autoComplete="email"
              aria-label="Correo electrónico"
              aria-invalid={
                !!errors.email || (emailTouched && !emailValidation.isValid)
              }
              aria-describedby={errors.email ? 'email-error' : undefined}
              disabled={isFetching}
            />
          </div>
          <div className="form-group">
            {errors.password &&
              errors.password.map((message, index) => (
                <span
                  key={index}
                  className="help-block text-danger pull-left"
                  id="password-error"
                >
                  <i className="fa fa-exclamation-circle" aria-hidden="true" />{' '}
                  {message}
                </span>
              ))}
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                className="form-control"
                placeholder="Contraseña"
                required=""
                value={password}
                onChange={handleChange}
                autoComplete="current-password"
                aria-label="Contraseña"
                aria-invalid={!!errors.password}
                aria-describedby={
                  errors.password ? 'password-error' : undefined
                }
                disabled={isFetching}
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                disabled={isFetching}
                aria-label={
                  showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'
                }
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  padding: '5px',
                  color: '#676a6c'
                }}
              >
                <i
                  className={showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={btnClass}
            disabled={isFetching || (emailTouched && !emailValidation.isValid)}
          >
            {isFetching ? (
              <>
                <i className="fa fa-spinner fa-spin" aria-hidden="true" />{' '}
                Iniciando sesión...
              </>
            ) : (
              'Iniciar sesión'
            )}
          </button>
        </form>

        {/* Aria-live region para screen readers */}
        <div role="status" aria-live="polite" className="sr-only">
          {isFetching ? 'Iniciando sesión, por favor espere...' : ''}
        </div>
      </div>
    </div>
  )
}

LoginPage.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  errors: PropTypes.shape({
    non_field_errors: PropTypes.array,
    detail: PropTypes.string,
    email: PropTypes.array,
    password: PropTypes.array
  }),
  isFetching: PropTypes.bool.isRequired,
  login: PropTypes.func.isRequired
}

const mapStateToProps = state => state.login

const mapDispatchToProps = {
  login: loginUser
}

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage)
